import { Anchor, Button, Group, Image, Input, Text } from "@mantine/core";
import dayjs from "dayjs";
import { type SubmitHandler, useForm } from "react-hook-form";
import { IconCheck } from "@tabler/icons-react";
import { useIsOnline, usePostApi } from "src/hooks";
import { CompactUserSettingsContainer } from "src/pages/Settings/AccountDetails/AccountDetails.styled";
import styles from "src/pages/Settings/AccountDetails/AccountDetails.module.scss";
import { type IAccountDetailsForm } from "src/pages/Settings/AccountDetails/AccountDetails.types";
import { Notification } from "src/utils/Notification";

interface Props {
  formData: IAccountDetailsForm;
  refetch: () => void;
}

interface DSSFormValues {
  dssToken: string;
}

const DSSPromoSection = ({ formData, refetch }: Props) => {
  const isOnline = useIsOnline();
  const { post, isLoading } = usePostApi();
  const { register, handleSubmit, reset } = useForm<DSSFormValues>({
    defaultValues: { dssToken: formData.dssToken },
  });

  const onSubmit: SubmitHandler<DSSFormValues> = async data => {
    await post({
      request: "user-save-account",
      body: {
        params: {
          ...formData,
          dssToken: data.dssToken,
        },
      },
      onSuccess: () => {
        reset(data);
        Notification(
          "Success",
          "DSS settings have been successfully changed.",
          "green",
        );
        refetch();
      },
    });
  };

  return (
    <CompactUserSettingsContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Anchor
          href="https://digitalseaservice.com/"
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
          underline="hover">
          <Image src="assets/DSS.png" maw={180} mb={4} />
          <Text className={styles.sectionTitle} component="span" display="block">
            Streamline your sea time.
          </Text>
          <Text size="sm" c="dimmed" mt={4}>
            Log, track, and verify your sea service effortlessly — all in one
            place, for MCA, PYA, USCG, Nautilus, and more. <br />
            <br />
            LJ eLogbooks Integration coming soon...
          </Text>
        </Anchor>
        <Input.Wrapper mt="sm" label="DSS API token">
          <Input {...register("dssToken")} readOnly={!isOnline} />
        </Input.Wrapper>
        <Text
          c={formData.dssActive ? "text" : "grayText"}
          size="sm"
          mt={5}
          style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {formData.dssActive
            ? `DSS Joined on ${dayjs(formData.dssJoinedDate).format("DD MMM YYYY")}`
            : "Not connected"}
          {formData.dssActive && <IconCheck size={16} color="green" />}
        </Text>
        {isOnline && (
          <Group justify="flex-end" mt="sm">
            <Button
              type="submit"
              variant="outline"
              size="xs"
              loading={isLoading}>
              Save
            </Button>
          </Group>
        )}
      </form>
    </CompactUserSettingsContainer>
  );
};

export default DSSPromoSection;
